import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  ArrowLeft, 
  Settings, 
  CreditCard, 
  Shield, 
  CheckCircle, 
  Package, 
  Key,
  Globe,
  RefreshCw,
  Zap,
  DollarSign,
  Users,
  Download,
  AlertTriangle
} from 'lucide-react';
import StripeConfig from '../components/StripeConfig';
import { usePermissions } from '../hooks/usePermissions';
import stripeApiService from '../services/stripeApiService';

const StripeConfigPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const [activeModule, setActiveModule] = useState('configuration');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Utiliser le système de permissions avancé pour vérifier l'accès à la configuration des paiements
  const { hasPermission, loading } = usePermissions({ userId: user?.uid });

  // Vérifier si l'utilisateur a la permission de gérer les paiements
  const canManagePayments = hasPermission({ resource: 'payments', action: 'manage' });

  // Modules disponibles
  const modules = [
    {
      id: 'configuration',
      label: 'Configuration API',
      icon: Key,
      description: 'Configuration des clés Stripe',
      color: 'blue'
    },
    {
      id: 'webhook',
      label: 'Webhook',
      icon: Globe,
      description: 'Configuration du webhook Stripe',
      color: 'green'
    },
    {
      id: 'produits',
      label: 'Gestion des produits',
      icon: Package,
      description: 'Produits et plans d\'abonnement',
      color: 'purple'
    },
    {
      id: 'statistiques',
      label: 'Statistiques',
      icon: DollarSign,
      description: 'Analytics et performances',
      color: 'orange'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={32} className="animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600 dark:text-gray-400">Chargement des permissions...</p>
        </div>
      </div>
    );
  }

  // Rendu du module actif
  const renderModule = () => {
    switch (activeModule) {
      case 'configuration':
        return <StripeConfig canManagePayments={canManagePayments} />;
      case 'webhook':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-6">
              <Globe className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Configuration Webhook</h2>
                <p className="text-gray-600 dark:text-gray-400">Configuration du webhook Stripe pour les événements en temps réel</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
                  <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3">URL du Webhook</h3>
                  <code className="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-3 py-2 rounded text-sm block break-all">
                    https://us-central1-{process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/handleStripeWebhook
                  </code>
                  <p className="text-green-700 dark:text-green-300 text-sm mt-2">
                    Copiez cette URL dans votre dashboard Stripe
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Événements à écouter</h3>
                  <ul className="text-blue-700 dark:text-blue-300 text-sm space-y-2">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>invoice.payment_succeeded</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>invoice.payment_failed</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>customer.subscription.created</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>customer.subscription.updated</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>customer.subscription.deleted</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-3">Instructions</h3>
                  <ol className="text-yellow-700 dark:text-yellow-300 text-sm space-y-3">
                    <li className="flex items-start">
                      <span className="bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                      <span>Allez dans le <strong>dashboard Stripe</strong> → Developers → Webhooks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                      <span>Cliquez sur <strong>"Add endpoint"</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                      <span>Collez l'URL du webhook ci-dessus</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">4</span>
                      <span>Sélectionnez les événements listés</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">5</span>
                      <span>Copiez le <strong>Signing Secret</strong> dans la configuration API</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Statut du webhook</h4>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span>Endpoint configuré</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'produits':
        return (
          <div className="space-y-6">
            {/* Section de gestion des produits */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <Package className="w-8 h-8 text-purple-500 mr-3" />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gestion des produits</h2>
                    <p className="text-gray-600 dark:text-gray-400">Configuration des produits et plans d'abonnement</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/stripe-products')}
                  className="flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Ouvrir la gestion des produits
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-6">
                    <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-3">Fonctionnalités disponibles</h3>
                    <ul className="text-purple-700 dark:text-purple-300 text-sm space-y-2">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>Création et modification de produits</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>Configuration des prix (mensuel, annuel)</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>Gestion de l'activation/désactivation</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>Vue d'ensemble des plans</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                    <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3">Workflow recommandé</h3>
                    <ol className="text-blue-700 dark:text-blue-300 text-sm space-y-3">
                      <li className="flex items-start">
                        <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                        <span>Créez d'abord les <strong>produits</strong> (plans d'abonnement)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                        <span>Ajoutez les <strong>prix</strong> pour chaque produit</span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                        <span>Vérifiez la vue d'ensemble dans l'onglet <strong>Plans</strong></span>
                      </li>
                      <li className="flex items-start">
                        <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">4</span>
                        <span>Testez les abonnements avec les nouveaux produits</span>
                      </li>
                    </ol>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                    <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-3">Bonnes pratiques</h3>
                    <ul className="text-yellow-700 dark:text-yellow-300 text-sm space-y-2">
                      <li className="flex items-start">
                        <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Utilisez des noms clairs pour les produits</span>
                      </li>
                      <li className="flex items-start">
                        <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Créez des prix pour différentes périodes</span>
                      </li>
                      <li className="flex items-start">
                        <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Testez toujours en mode test avant la production</span>
                      </li>
                      <li className="flex items-start">
                        <CreditCard className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Gardez les produits inactifs pour les anciens plans</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Statut de synchronisation</h4>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span>Interface de gestion disponible</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        );
      case 'statistiques':
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-6">
              <DollarSign className="w-8 h-8 text-orange-500 mr-3" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Statistiques Stripe</h2>
                <p className="text-gray-600 dark:text-gray-400">Analytics et performances des paiements</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-orange-800 dark:text-orange-300">Revenus mensuels</h3>
                  <DollarSign className="w-6 h-6 text-orange-500" />
                </div>
                <p className="text-3xl font-bold text-orange-800 dark:text-orange-300">0 €</p>
                <p className="text-orange-700 dark:text-orange-400 text-sm mt-2">Aucune transaction ce mois</p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-300">Abonnements actifs</h3>
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <p className="text-3xl font-bold text-blue-800 dark:text-blue-300">0</p>
                <p className="text-blue-700 dark:text-blue-400 text-sm mt-2">Aucun abonnement actif</p>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-green-800 dark:text-green-300">Taux de réussite</h3>
                  <Zap className="w-6 h-6 text-green-500" />
                </div>
                <p className="text-3xl font-bold text-green-800 dark:text-green-300">0%</p>
                <p className="text-green-700 dark:text-green-400 text-sm mt-2">Aucune transaction traitée</p>
              </div>
            </div>

            <div className="mt-8 bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Informations de suivi</h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p>Les statistiques seront disponibles après la première transaction réussie.</p>
                <p>Pour accéder aux statistiques détaillées, visitez votre <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">dashboard Stripe</a>.</p>
              </div>
            </div>
          </div>
        );
      default:
        return <StripeConfig canManagePayments={canManagePayments} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header mobile */}
      <div className="md:hidden bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 mr-3"
              aria-label="Ouvrir le menu"
            >
              <span className="text-xl dark:text-gray-300">☰</span>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Configuration Stripe</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">Gestion des paiements et abonnements</p>
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {modules.find(m => m.id === activeModule)?.icon && React.createElement(modules.find(m => m.id === activeModule).icon, { size: 20 })}
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Navigation latérale - Desktop */}
        <div className="hidden md:block w-64 bg-white dark:bg-gray-800 shadow-sm min-h-screen">
          <div className="p-6 border-b dark:border-gray-700">
            <div className="flex items-center">
              <CreditCard className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Stripe</h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Gestion des paiements</p>
              </div>
            </div>
          </div>
          
          <nav className="p-4">
            <ul className="space-y-2">
              {modules.map((module) => {
                const IconComponent = module.icon;
                return (
                  <li key={module.id}>
                    <button
                      onClick={() => setActiveModule(module.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors min-h-[60px] ${
                        activeModule === module.id
                          ? `bg-${module.color}-50 dark:bg-${module.color}-900/20 text-${module.color}-700 dark:text-${module.color}-300 border-l-4 border-${module.color}-500 dark:border-${module.color}-400`
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <IconComponent className="w-5 h-5 mr-3" />
                        <div>
                          <div className="font-medium text-sm md:text-base">{module.label}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{module.description}</div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Informations système */}
          <div className="p-4 border-t dark:border-gray-700 mt-4">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p><strong>OMAD SERVICES</strong></p>
              <p className="mt-1">Système de paiement Stripe</p>
              <p>Version 1.0</p>
            </div>
          </div>
        </div>

        {/* Navigation mobile - Overlay */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50 dark:bg-opacity-70">
            <div className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 shadow-lg">
              <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Navigation</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                  aria-label="Fermer le menu"
                >
                  <span className="text-xl dark:text-gray-300">✕</span>
                </button>
              </div>
              
              <nav className="p-4">
                <ul className="space-y-2">
                  {modules.map((module) => {
                    const IconComponent = module.icon;
                    return (
                      <li key={module.id}>
                        <button
                          onClick={() => {
                            setActiveModule(module.id);
                            setMobileMenuOpen(false);
                          }}
                          className={`w-full text-left p-3 rounded-lg transition-colors min-h-[60px] ${
                            activeModule === module.id
                              ? `bg-${module.color}-50 dark:bg-${module.color}-900/20 text-${module.color}-700 dark:text-${module.color}-300 border-l-4 border-${module.color}-500 dark:border-${module.color}-400`
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center">
                            <IconComponent className="w-5 h-5 mr-3" />
                            <div>
                              <div className="font-medium">{module.label}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{module.description}</div>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Contenu principal */}
        <div className="flex-1 md:ml-0">
          {/* En-tête desktop */}
          <div className="hidden md:block bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuration Stripe</h1>
                  <p className="text-gray-600 dark:text-gray-400">Gestion complète des paiements et abonnements</p>
                </div>
                <button 
                  onClick={() => navigate(-1)} 
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ArrowLeft size={24} className="text-gray-700 dark:text-gray-300" />
                </button>
              </div>
            </div>
          </div>

          {/* Informations pour les utilisateurs sans permission */}
          {!canManagePayments && (
            <div className="p-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Shield className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mr-2" />
                  <h2 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300">
                    Accès restreint
                  </h2>
                </div>
                <p className="text-yellow-700 dark:text-yellow-400 mb-4">
                  Cette fonctionnalité est réservée aux administrateurs. Seuls les utilisateurs avec la permission de gestion des paiements peuvent configurer les paramètres Stripe.
                </p>
                <div className="bg-yellow-100 dark:bg-yellow-800/30 rounded-lg p-4">
                  <h3 className="font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                    Pour configurer Stripe :
                  </h3>
                  <ul className="text-yellow-700 dark:text-yellow-400 text-sm space-y-1">
                    <li>• Connectez-vous avec un compte administrateur</li>
                    <li>• Contactez l'administrateur système pour obtenir les permissions nécessaires</li>
                    <li>• Les clés Stripe sont nécessaires pour activer les paiements</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Contenu pour les utilisateurs autorisés */}
          {canManagePayments && (
            <div className="p-6">
              {renderModule()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeConfigPage;
